import { useEffect, useState } from 'react';
import evsMark from '@svg/evs-mark.svg'; // 정식 EVS 심볼(원본 EVS_logo_Final.svg에서 심볼만 크롭·네이비화). 손그림 로고 대체(2026-09-08).
import s from './LcdDisplay.module.scss';

// ── 현장 LCD 실시간 표시(애니메이션) — 2026-09-08 사용자 ────────────────────────────────
//   실제 AI 체감온도계 LCD(최종3 standalone) 재현: 흰 배경 + 헤더(로고·ID·시각·아이콘) +
//   온도/습도 카드 + 체감온도(WBGT)/폭염단계 + 필요조치 바. 정상→관심→주의 순환(색·수치·조치 전환).
//   ★단계 임계값(사용자 확정): 정상<31 · 관심≥31 · 주의≥33 (LCD는 3단계=폭염단계 0/1/2 스프라이트).
//   색: 정상=녹 / 관심=주황 / 주의=적. 필요조치 문구=실제 LCD 표기(폭염 대응지침 기반).
//   네이티브 구현(원본 22MB 스프라이트 HTML 대신) → 배포 가능. reduce-motion 시 순환 정지.
const STATES = [
  { level: '정상', color: '#22a45f', wbgt: '28.6', temp: '26.3', hum: '58', mark: 'ok', action: '수분 수시 섭취 · 정상 작업' },
  { level: '관심', color: '#f47a1f', wbgt: '31.8', temp: '30.4', hum: '62', mark: 'warn', action: '매시간 10분 그늘 휴식 · 물 자주 마시기' },
  { level: '주의', color: '#e5342b', wbgt: '34.2', temp: '32.6', hum: '66', mark: 'stop', action: '매시간 15분 휴식 · 14–17시 작업단축 · 2인 1조' },
];
const WDAY = ['일', '월', '화', '수', '목', '금', '토'];

export default function LcdDisplay() {
  const [i, setI] = useState(0);
  const [now, setNow] = useState('14:06');
  const [ymd, setYmd] = useState('2026.09.08 (화)');

  useEffect(() => {
    const tick = () => {
      const d = new Date();
      const p = (n: number) => String(n).padStart(2, '0');
      setNow(`${p(d.getHours())}:${p(d.getMinutes())}`);
      setYmd(`${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} (${WDAY[d.getDay()]})`);
    };
    tick();
    const clk = window.setInterval(tick, 30000);
    let cyc = 0;
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      cyc = window.setInterval(() => setI((v) => (v + 1) % STATES.length), 2600);
    }
    return () => { window.clearInterval(clk); if (cyc) window.clearInterval(cyc); };
  }, []);
  const st = STATES[i];

  return (
    <div className={s.lcd} style={{ ['--c' as string]: st.color } as React.CSSProperties}>
      {/* 헤더: 로고 · 센서ID · 날짜/시각 · 상태 아이콘 */}
      <div className={s.head}>
        <span className={s.brand}>
          {/* 정식 EVS 심볼 로고(evs-mark.svg). 손그림 path가 실제 로고와 달라 원본 크롭본으로 교체(2026-09-08 사용자). */}
          <img className={s.mark} src={evsMark} alt="EVS" />
          <span className={s.sid}>40617</span>
        </span>
        <span className={s.clock}><em>{ymd}</em><b>{now}</b></span>
        <span className={s.stat}>
          <svg width="20" height="16" viewBox="0 0 20 16" fill="currentColor" aria-hidden="true"><rect x="1" y="10" width="3" height="5" rx="1"/><rect x="6" y="7" width="3" height="8" rx="1"/><rect x="11" y="4" width="3" height="11" rx="1"/><rect x="16" y="1" width="3" height="14" rx="1"/></svg>
          <svg width="20" height="16" viewBox="0 0 22 16" fill="currentColor" aria-hidden="true"><path d="M6 13a4 4 0 0 1-.4-8A6 6 0 0 1 17 6.5 3.5 3.5 0 0 1 16.5 13H6Z"/></svg>
          <svg width="17" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><rect x="5" y="5" width="8" height="8" rx="1.5"/><path d="M8 5V2M10 5V2M8 16v-3M10 16v-3M5 8H2M5 10H2M16 8h-3M16 10h-3"/></svg>
        </span>
      </div>

      {/* 온도 · 습도 — 원본 standalone LCD와 동일한 lucide 아이콘(Thermometer/Droplet). 이모지 대체(2026-09-08 사용자). */}
      <div className={s.env}>
        <div className={`${s.envCard} ${s.temp}`}>
          <span><svg className={s.envIc} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 14.76V4a2 2 0 0 0-4 0v10.76a4 4 0 1 0 4 0z" /></svg>온도</span><b>{st.temp}°</b>
        </div>
        <div className={`${s.envCard} ${s.hum}`}>
          <span><svg className={s.envIc} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" /></svg>습도</span><b>{st.hum}%</b>
        </div>
      </div>

      {/* 체감온도(WBGT) · 폭염단계 */}
      <div className={s.main}>
        <div className={s.wbgtCard}>
          <span className={s.wbgtLabel}>체감온도 <em>WBGT</em></span>
          <span className={s.wbgt}>{st.wbgt}<i>℃</i></span>
        </div>
        <div className={s.stageCard}>
          <span className={s.stageLabel}>폭염단계</span>
          <span className={s.stageName}>{st.level}</span>
        </div>
      </div>

      {/* 필요조치 바 */}
      <div className={s.action}>
        <span className={s.actionIcon}>
          {st.mark === 'ok'
            ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/></svg>
            : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4 22 20H2Z"/><path d="M12 10v5M12 18h.01"/></svg>}
        </span>
        <b className={s.actionLabel}>필요조치</b>
        <span className={s.actionText}>{st.action}</span>
      </div>
    </div>
  );
}
